import { z } from 'zod';
import { costActionType } from './credit.js';
import { userTier } from './user.js';
/**
 * Error handling contract (E12, CODE_GUIDELINES §16).
 *
 * ONE closed error-code set, used in every WS `err` envelope's `UserAlert.code`.
 * `message` is ALWAYS safe to show (no stacks/SQL/provider text) and is an
 * @xdocs/i18n catalog KEY (namespace `errors`/`alerts`), resolved to the
 * recipient's locale at the edge — never a hardcoded English literal (E18).
 */
export const errorCode = z.enum([
    'VALIDATION',
    'UNAUTHENTICATED',
    'FORBIDDEN', // the single role/ownership/ACL/impersonation-write-block denial
    'NOT_FOUND',
    'CONFLICT',
    'INSUFFICIENT_CREDITS',
    'TIER_REQUIRED',
    'RATE_LIMITED',
    'CONTENT_POLICY_VIOLATION',
    'PROMO_INVALID',
    'BAD_OP',
    'INTERNAL',
]);
/** Per-field inline error (form display). `path` is the dotted zod path. */
export const alertField = z.object({
    path: z.string(),
    message: z.string(), // catalog key, resolved at the edge
});
/**
 * The generic alert carried by an `err` envelope (E12). `message` + each
 * `fields[].message` are catalog keys.
 */
export const userAlertSchema = z.object({
    code: errorCode,
    message: z.string(),
    fields: z.array(alertField).optional(),
});
/**
 * Fixed policy-category vocabulary (§16.2). Surfaced labels are these enum
 * values mapped to catalog keys — NEVER raw model text.
 */
export const policyCategory = z.enum([
    'hate',
    'harassment',
    'sexual',
    'sexual_minors',
    'violence',
    'self_harm',
    'illegal',
    'malware',
    'pii',
]);
/**
 * `CONTENT_POLICY_VIOLATION` always carries the violated terms + a policy URL
 * (§16.2). Returned identically at BOTH gates (generation-time and share-time),
 * built only by `contentPolicyAlert`.
 */
export const contentPolicyAlertSchema = z.object({
    code: z.literal('CONTENT_POLICY_VIOLATION'),
    message: z.string(), // human, non-scary, names the term(s) — catalog key
    violatedTerms: z.array(policyCategory),
    policyUrl: z.string().url(),
});
/**
 * `TIER_REQUIRED` — THE `requirePro` contract (M3 WS-KILLER/WS-EDITOR/WS-BILL
 * §21: expand/cut/difficulty-elevate/translate (F2/F3/F4/F17) and illustrate
 * (F1) are PRO-only actions). `requiredTier` is future-proofed as `UserTier`
 * even though every current caller passes `'pro'` — never hardcode the
 * literal in a consumer, read it off this alert. `actionType` is the
 * `costActionType` the caller attempted, when the gate fired on a specific
 * chargeable action (omitted for a non-action tier gate, e.g. a PRO-only
 * settings toggle). Built only by {@link tierRequiredAlert}, mirroring
 * {@link contentPolicyAlert}'s one-builder discipline.
 */
export const tierRequiredAlertSchema = z.object({
    code: z.literal('TIER_REQUIRED'),
    message: z.string(), // human, names what's gated — catalog key
    requiredTier: userTier,
    actionType: costActionType.optional(),
});
/**
 * The union an `err` envelope payload may hold. `contentPolicyAlertSchema`
 * and `tierRequiredAlertSchema` are listed BEFORE `userAlertSchema`: their
 * extra required fields make them the specific cases, and `userAlertSchema`
 * (whose `code` enum also contains `CONTENT_POLICY_VIOLATION`/
 * `TIER_REQUIRED`) would otherwise greedily match and strip those fields. A
 * plain alert lacking them falls through to `userAlertSchema`.
 */
export const errorAlert = z.union([contentPolicyAlertSchema, tierRequiredAlertSchema, userAlertSchema]);
/** Catalog key used when a validation error has no field-level message. */
export const GENERIC_VALIDATION_KEY = 'errors:validation.generic';
/**
 * THE single mapper (E6/§3.2). Turns a `ZodError` into the alert both the WS
 * `err` envelope and the toast component understand. Field messages come from
 * each schema's own `{ message }` — which are catalog keys — so the raw ZodError
 * is never shown. Localization happens where the alert is rendered (SPA `t()`)
 * or serialized (server, against the socket's locale).
 */
export function toUserAlert(err) {
    const fields = err.issues.map((issue) => ({
        path: issue.path.join('.'),
        message: issue.message,
    }));
    const first = fields[0];
    return {
        code: 'VALIDATION',
        message: first?.message ?? GENERIC_VALIDATION_KEY,
        fields,
    };
}
/**
 * THE single builder for a content-policy refusal (§16.2). When no category
 * matched, falls back to the generic `disallowed` label key so the shape is
 * always well-formed.
 */
export function contentPolicyAlert(violatedTerms, policyUrl) {
    return {
        code: 'CONTENT_POLICY_VIOLATION',
        message: violatedTerms.length > 0
            ? 'errors:content_policy.violation'
            : 'errors:content_policy.disallowed',
        violatedTerms,
        policyUrl,
    };
}
/**
 * THE single builder for a `TIER_REQUIRED` refusal — the `requirePro`
 * contract every server op that gates a PRO-only action (F2/F3/F4/F17/F1
 * illustrate) returns on an `err` envelope when the caller's `users.tier`
 * doesn't meet `requiredTier`. `requirePro`-the-helper (server middleware/
 * guard) lives in `@xdocs/core`/`apps/server` (WS-BILL ownership) and is
 * expected to build its refusal with this function — never hand-roll the
 * shape at a call site.
 */
export function tierRequiredAlert(requiredTier, actionType) {
    return {
        code: 'TIER_REQUIRED',
        message: 'errors:tier.required',
        requiredTier,
        ...(actionType !== undefined ? { actionType } : {}),
    };
}
